import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Gift } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);

  // Capture ?ref=CODE from the URL on mount and persist it so a page reload doesn't lose it.
  useEffect(() => {
    const refFromUrl = searchParams.get("ref");
    if (refFromUrl) {
      setReferralCode(refFromUrl);
      try {
        localStorage.setItem("aura_ref", refFromUrl);
      } catch { /* empty */ }
    } else {
      try {
        const stored = localStorage.getItem("aura_ref");
        if (stored) setReferralCode(stored);
      } catch { /* empty */ }
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, referralCode || null);
      toast.success(referralCode ? "Welcome to Aura — your friend earns Plus when you subscribe" : "Welcome to Aura");
      try { localStorage.removeItem("aura_ref"); } catch { /* empty */ }
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0C0E] text-[#F5F3EE]">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <div>
            <div className="overline mb-3">Start free</div>
            <h1 className="font-display text-5xl leading-none">Create your Aura.</h1>
            <p className="text-[#A8ADA5] mt-3 text-sm">First scan is on us. No card. No catch.</p>
            {referralCode && (
              <div
                className="mt-4 inline-flex items-center gap-2 border border-[#D5FF3F]/30 bg-[#D5FF3F]/5 rounded-full px-3 py-1.5"
                data-testid="register-ref-banner"
              >
                <Gift size={11} className="text-[#D5FF3F]" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                  Invited via <span className="text-[#F5F3EE]">{referralCode}</span> · Your friend earns Plus when you subscribe
                </span>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Full name</Label>
              <Input id="name" data-testid="register-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-sm h-11" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Email</Label>
              <Input id="email" data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-sm h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Password · min 6</Label>
              <Input id="password" data-testid="register-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-sm h-11" />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="register-submit-btn"
            disabled={loading}
            className="w-full h-12 rounded-full btn-lime"
          >
            {loading ? "Creating…" : <>Create account <ArrowUpRight size={15} className="ml-1.5" /></>}
          </Button>
          <p className="text-xs text-[#6D716C] leading-relaxed">
            By continuing you agree to our{" "}
            <Link to="/terms" className="text-[#D5FF3F] underline underline-offset-4">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-[#D5FF3F] underline underline-offset-4">Privacy &amp; Disclaimer</Link>.
            Aura is a self-service educational tool. You can dispute credit items yourself for free at any time.
          </p>
          <div className="text-sm text-[#A8ADA5] text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-[#D5FF3F] underline underline-offset-4" data-testid="register-to-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>
      <div className="hidden lg:flex relative overflow-hidden flex-col justify-between p-12 bg-[#131518] border-l border-[#26282C] order-1 lg:order-2">
        <div className="grid-bg absolute inset-0 opacity-30" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#D5FF3F]/10 blur-[120px]" />
        <Link to="/" className="relative flex items-center gap-2.5 self-end" data-testid="register-brand">
          <span className="font-display text-2xl">Aura Credit</span>
          <div className="w-8 h-8 rounded-sm bg-[#D5FF3F] flex items-center justify-center">
            <span className="font-display italic text-[#0B0C0E] text-lg leading-none">a</span>
          </div>
        </Link>
        <div className="relative">
          <div className="overline mb-6">The promise</div>
          <p className="font-display text-5xl leading-[0.95] max-w-md">
            A quieter mind. A cleaner report. A score that opens doors again.
          </p>
        </div>
        <div className="relative text-xs text-[#6D716C] font-mono uppercase tracking-widest">
          FCRA-aware · CROA-informed
        </div>
      </div>
    </div>
  );
}
